import { Badge, Box, Group } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { useAuth } from '../../lib/auth-context';

/**
 * AppNavTabs — a full-width horizontal tab strip that rides in the AppHeader on
 * lg+, giving the authenticated app a top-of-page nav bar alongside the left
 * sidebar (requested so the primary destinations are always one glance away at
 * the top of the screen, matching the marketing site's top nav).
 *
 * It surfaces the universal primary destinations (Dashboard, Inbox, Browse
 * venues, Browse artists, Events); the hat-specific sections and the long admin
 * list stay in the sidebar. The Dashboard target is hat-aware, mirroring the
 * sidebar / UserMenu logic. Below lg the strip is hidden — the burger drawer
 * (which surfaces the full sidebar) owns navigation there.
 */

const NAV_HOVER_STYLE_ID = 'wwaave-topnav-hover';
function ensureNavHoverStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(NAV_HOVER_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = NAV_HOVER_STYLE_ID;
  style.textContent = `
    .wwaave-topnav-tab:not([aria-current="page"]):hover {
      /* ink-9 is scheme-flipped by warmth.css (near-black in light, near-white
         in dark), so it reads on the header in both. Do NOT use light-dark with
         gray-1 here — warmth.css remaps gray-1 to a dark charcoal in dark mode,
         which vanished against the dark header. */
      color: var(--mantine-color-ink-9) !important;
      border-bottom-color: light-dark(var(--wwaave-brand-border), var(--mantine-color-dark-3)) !important;
    }
  `;
  document.head.appendChild(style);
}

type TabTo =
  | '/dashboard'
  | '/host/dashboard'
  | '/admin'
  | '/inbox'
  | '/browse'
  | '/browse/artists'
  | '/events';

export function AppNavTabs() {
  ensureNavHoverStyle();
  const { user, activeHat } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Inbox unread total. Shares the ['inbox'] query cache with the sidebar and
  // /inbox, so this never triggers an extra fetch. Fail-quiet.
  const inboxQuery = useQuery({
    queryKey: ['inbox'],
    queryFn: async (): Promise<{ threads: Array<{ unreadCount: number }> }> => {
      const res = await fetch('/api/threads', { credentials: 'include' });
      if (!res.ok) return { threads: [] };
      return res.json();
    },
    enabled: user !== null,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  const inboxUnread = (inboxQuery.data?.threads ?? []).reduce(
    (sum, t) => sum + (t.unreadCount > 0 ? 1 : 0),
    0,
  );

  const dashboardTo: '/admin' | '/host/dashboard' | '/dashboard' =
    activeHat === 'admin' ? '/admin' : activeHat === 'host' ? '/host/dashboard' : '/dashboard';

  const dashboardActive =
    dashboardTo === '/admin'
      ? pathname === '/admin' || pathname === '/admin/'
      : pathname === dashboardTo || pathname === `${dashboardTo}/`;

  return (
    <Group
      component="nav"
      aria-label="Primary"
      gap={4}
      wrap="nowrap"
      style={{
        flex: 1,
        minWidth: 0,
        overflowX: 'auto',
        // Hide the horizontal scrollbar — the strip scrolls on narrow lg widths
        // but should read as a clean nav bar, not a scroll area.
        scrollbarWidth: 'none',
        marginInline: 8,
      }}
    >
      <TabLink to={dashboardTo} active={dashboardActive}>
        Dashboard
      </TabLink>
      <TabLink
        to="/inbox"
        active={pathname === '/inbox' || pathname.startsWith('/inbox/')}
        badge={inboxUnread > 0 ? inboxUnread : undefined}
      >
        Inbox
      </TabLink>
      <TabLink
        to="/browse"
        active={
          pathname === '/browse' ||
          (pathname.startsWith('/browse/') && !pathname.startsWith('/browse/artists'))
        }
      >
        Browse venues
      </TabLink>
      <TabLink
        to="/browse/artists"
        active={pathname === '/browse/artists' || pathname.startsWith('/browse/artists/')}
      >
        Browse artists
      </TabLink>
      <TabLink to="/events" active={pathname === '/events' || pathname.startsWith('/events/')}>
        Events
      </TabLink>
    </Group>
  );
}

function TabLink({
  to,
  active,
  badge,
  children,
}: {
  to: TabTo;
  active: boolean;
  badge?: number | undefined;
  children: ReactNode;
}) {
  return (
    <Box
      component={Link}
      to={to}
      className="wwaave-topnav-tab"
      aria-current={active ? 'page' : undefined}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        whiteSpace: 'nowrap',
        padding: '0 12px',
        height: 40,
        borderBottom: active
          ? '2px solid var(--mantine-color-brand-6)'
          : '2px solid transparent',
        color: active
          ? 'light-dark(var(--mantine-color-brand-7), var(--mantine-color-brand-4))'
          : 'var(--mantine-color-ink-7)',
        fontSize: 14,
        fontWeight: active ? 600 : 500,
        textDecoration: 'none',
        transition: 'color 120ms ease, border-color 120ms ease',
      }}
    >
      {children}
      {badge !== undefined ? (
        <Badge
          size="sm"
          variant="filled"
          color="brand.6"
          radius="xl"
          aria-label={`${badge} unread`}
          style={{ flexShrink: 0, minWidth: 20, height: 20, paddingInline: 6 }}
        >
          {badge > 99 ? '99+' : badge}
        </Badge>
      ) : null}
    </Box>
  );
}
